import { z } from 'zod';
const usage = z
    .object({
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    totalTokens: z.number().optional(),
    reasoningTokens: z.number().optional(),
    cachedInputTokens: z.number().optional(),
})
    .loose();
const error = z
    .object({ name: z.string(), message: z.string(), category: z.string() })
    .loose()
    .nullable();
const runSchema = z
    .object({
    runId: z.string(),
    rootAgentId: z.string(),
    status: z.string(),
    startedAt: z.number(),
    endedAt: z.number().nullable(),
    usage: usage.optional(),
    error: error.optional(),
})
    .loose();
const agentSchema = z
    .object({
    nodeId: z.string(),
    parentNodeId: z.string().nullable(),
    runId: z.string(),
    agentId: z.string(),
    name: z.string(),
    kind: z.string(),
    status: z.string(),
    startedAt: z.number(),
    endedAt: z.number().nullable(),
    model: z.string().optional(),
    usage: usage.optional(),
    error: error.optional(),
})
    .loose();
// Hosts also emit a `data-handoff` of their own shape (`{ from, to }`); only the framework's carries
// node ids, which is what tells the two apart.
const handoffSchema = z
    .object({
    runId: z.string(),
    fromNodeId: z.string(),
    toNodeId: z.string(),
    fromAgentId: z.string(),
    toAgentId: z.string(),
    reason: z.string().optional(),
})
    .loose();
const guardrailSchema = z
    .object({
    guardrailRunId: z.string(),
    nodeId: z.string(),
    guardrailName: z.string(),
    stage: z.enum(['input', 'output']),
    status: z.enum(['running', 'passed', 'failed']),
})
    .loose();
const suspensionSchema = z.object({ runId: z.string(), token: z.string() }).loose();
/** A `data-compaction` payload, for a host that reads one off something other than the stream. */
export const compactionDataSchema = z
    .object({
    contextId: z.string(),
    checkpointId: z.string(),
    coveredCount: z.number(),
    coveredTurns: z.number(),
    model: z.string(),
    usage: z.object({
        inputTokens: z.number(),
        cachedInputTokens: z.number(),
        outputTokens: z.number(),
    }),
})
    .loose();
const compactingSchema = z
    .object({
    contextId: z.string(),
    nodeId: z.string().optional(),
    state: z.enum(['running', 'done']).optional(),
})
    .loose();
/** Recognises the framework's run-tree parts; anything else, or a malformed payload, is `null`. */
export function parseFrameworkPart(part) {
    switch (part.type) {
        case 'data-run': {
            const parsed = runSchema.safeParse(part.data);
            return parsed.success ? { kind: 'run', data: parsed.data } : null;
        }
        case 'data-agent': {
            const parsed = agentSchema.safeParse(part.data);
            return parsed.success ? { kind: 'agent', data: parsed.data } : null;
        }
        case 'data-handoff': {
            const parsed = handoffSchema.safeParse(part.data);
            return parsed.success ? { kind: 'handoff', data: parsed.data } : null;
        }
        case 'data-guardrail': {
            const parsed = guardrailSchema.safeParse(part.data);
            return parsed.success ? { kind: 'guardrail', data: parsed.data } : null;
        }
        case 'data-suspension': {
            const parsed = suspensionSchema.safeParse(part.data);
            return parsed.success ? { kind: 'suspension', data: parsed.data } : null;
        }
        case 'data-compaction': {
            const parsed = compactionDataSchema.safeParse(part.data);
            return parsed.success ? { kind: 'compaction', data: parsed.data } : null;
        }
        // The wait before a turn sends no state and is ended by other parts; one inside a tool loop
        // ends itself with `done`.
        case 'data-compacting': {
            const parsed = compactingSchema.safeParse(part.data);
            if (!parsed.success)
                return null;
            const { state, nodeId } = parsed.data;
            return { kind: 'compacting', state: state ?? 'running', ...(nodeId ? { nodeId } : {}) };
        }
        default:
            return null;
    }
}
